import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { AsanaSourceSettingsApi } from "./AsanaSourceSettings.Api";
import type { AsanaSourceSettingsViewModel } from "./AsanaSourceSettings.ViewModel.types";
import type {
  AsanaConfiguration,
  AsanaWorkspace,
} from "./AsanaSourceSettings.types";

const NONE_OPTION = "__none";

export const useAsanaSourceSettingsViewModel = (
  model: SourceSettingsModel,
): AsanaSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const autoSelectedWorkspaceForConnectionRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [workspaces, setWorkspaces] = useState<AsanaWorkspace[]>([]);
  const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [projects, setProjects] = useState<
    import("./AsanaSourceSettings.types").AsanaProject[]
  >([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Asana && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<AsanaConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const configurationRef = useRef(configuration);
  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  const selectedWorkspaceGid = configuration.workspace?.gid ?? "";

  // Initialise defaults on first render for this connection
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      autoSelectedWorkspaceForConnectionRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<AsanaConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: cfg.projects ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // Reset on profile/auth change
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setWorkspaces([]);
    setProjects([]);
    autoSelectedWorkspaceForConnectionRef.current = null;

    updateConnectionConfiguration<AsanaConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        workspace: undefined,
        projects: [],
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  // Load workspaces
  useEffect(() => {
    const loadWorkspaces = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setWorkspaces([]);
        setWorkspacesLoaded(true);
        return;
      }

      const asanaApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<AsanaSourceSettingsApi>(
          SourceTypeIds.Asana,
        );
      if (!asanaApi) {
        setWorkspaces([]);
        setWorkspacesLoaded(true);
        return;
      }

      setWorkspacesLoaded(false);
      setHasError(false);
      try {
        const nextWorkspaces = await asanaApi.getWorkspaces(
          model.boardId,
          editingConnectionId,
        );
        setWorkspaces(nextWorkspaces);

        const currentConfig = configurationRef.current;
        if (
          !currentConfig.workspace?.gid &&
          nextWorkspaces.length > 0 &&
          autoSelectedWorkspaceForConnectionRef.current !== editingConnectionId
        ) {
          autoSelectedWorkspaceForConnectionRef.current = editingConnectionId;
          const firstWorkspace = nextWorkspaces[0];
          updateConnectionConfiguration<AsanaConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              cfg.workspace?.gid
                ? cfg
                : { ...cfg, workspace: firstWorkspace, projects: [] },
            { isDefault: true },
          );
        }
      } catch {
        setWorkspaces([]);
        setHasError(true);
      } finally {
        setWorkspacesLoaded(true);
      }
    };

    void loadWorkspaces();
  }, [editingConnectionId, isVisible, model.boardId]);

  // Load projects when workspace changes
  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedWorkspaceGid) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      const asanaApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<AsanaSourceSettingsApi>(
          SourceTypeIds.Asana,
        );
      if (!asanaApi) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      setProjectsLoaded(false);
      try {
        const nextProjects = await asanaApi.getProjects(
          model.boardId,
          editingConnectionId,
          selectedWorkspaceGid,
        );
        setProjects(nextProjects);
      } catch {
        setProjects([]);
      } finally {
        setProjectsLoaded(true);
      }
    };

    void loadProjects();
  }, [editingConnectionId, isVisible, model.boardId, selectedWorkspaceGid]);

  return {
    isVisible,
    workspaces,
    workspacesLoaded,
    hasError,
    selectedWorkspaceGid: selectedWorkspaceGid || NONE_OPTION,
    selectedWorkspaceName:
      !selectedWorkspaceGid
        ? ""
        : (workspaces.find((w) => w.gid === selectedWorkspaceGid)?.name ?? ""),
    projects,
    projectsLoaded,
    selectedProjectGids: (configuration.projects ?? []).map((p) => p.gid),
    noneOptionValue: NONE_OPTION,

    onSelectWorkspace: (gid?: string) => {
      const selectedGid = gid ?? NONE_OPTION;
      const workspace = workspaces.find((w) => w.gid === selectedGid);

      updateConnectionConfiguration<AsanaConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          workspace: selectedGid === NONE_OPTION ? undefined : workspace,
          projects: [],
        }),
      );
    },

    onSelectProjects: (gids: string[]) => {
      const selectedProjects = projects.filter((p) => gids.includes(p.gid));
      updateConnectionConfiguration<AsanaConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, projects: selectedProjects }),
      );
    },
  };
};
